import React, { useState, useRef, useEffect } from 'react';
import { Shield, User, HelpCircle, Menu, X, LogOut, Bell, Search, ChevronDown, Settings, Info } from 'lucide-react';
import axios from 'axios';
import Avatar from '../../../services/Avatar';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  
  const profileRef = useRef(null);
  const helpRef = useRef(null);
  const notificationsRef = useRef(null);


  const Skeleton = ({ width = "w-8", height = "h-8", rounded = "rounded-full" }) => (
    <div className={`${width} ${height} ${rounded} bg-gray-300 animate-pulse`} />
  );



  useEffect(() => {
    const controller = new AbortController();
    const fetchUserProfile = async () => {
        try {
            const response = await axios.get('/users/me', { 
                withCredentials: true,
                signal: controller.signal // Attach signal to cancel duplicate requests
            });
            setCurrentUser(response.data);
        } catch (error) {
            if (axios.isCancel(error)) {
                console.log('Request canceled:', error.message);
            } else {
                console.error('Error fetching user profile:', error);
            }
        }
    };

    fetchUserProfile();

    return () => controller.abort(); // Cleanup function to cancel request on re-render
}, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
      if (helpRef.current && !helpRef.current.contains(event.target)) {
        setIsHelpOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setIsNotificationsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

 

  const handleLogout = async () => {
    try {
      const response = await fetch('/d-auth/logout', {
        method: 'POST', // or 'GET', depending on your backend implementation
        credentials: 'include', // include cookies with the request
        headers: {
          'Content-Type': 'application/json',
        },
      });
  
      if (response.ok) {       
        
        window.location.href = '/'; 
      } else {
        // If there's an error during logout
        const errorData = await response.json();
        //console.error('Logout failed', errorData);
        alert( errorData ||'Logout failed. Please try again.');
      }
    } catch (error) {
      //console.error('Error during logout:', error);
      alert('An error occurred. Please try again.');
    }
  };

  const navLinks = [
    { name: 'Dashboard', href: '#', current: true },
    /*{ name: 'Threats', href: '#', current: false },
    { name: 'Analytics', href: '#', current: false },
    { name: 'Reports', href: '#', current: false },*/
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-gray-900 to-gray-800 border-b border-gray-700 shadow-lg">
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo and brand */}
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="flex items-center">
                <Shield className="h-8 w-8 text-indigo-400" />
                <h1 className="ml-3 text-2xl font-bold text-white tracking-tight">
                  Threat<span className="text-indigo-400">Eye</span>
                </h1>
              </div>
            </div>
            
            {/* Desktop navigation links */}
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-4">
                {navLinks.map((link) => (
                  <a
                    key={link.name}
                    href={link.href}
                    className={`px-3 py-2 rounded-md text-sm font-medium ${
                      link.current
                        ? 'bg-gray-700 text-white'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    } transition duration-150 ease-in-out`}
                  >
                    {link.name}
                  </a>
                ))}
              </div>
            </div>
          </div>
          
          {/* Right side items */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Search */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search..."
                className="bg-gray-700 text-gray-300 text-sm rounded-lg pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-gray-600 w-64"
              />
            </div>
            
            {/* Notifications */}
            <div className="relative" ref={notificationsRef}>
              <button 
                onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                className="text-gray-300 hover:text-white p-1 rounded-full hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-indigo-500"
              >
                <span className="sr-only">View notifications</span>
                <div className="relative">
                  <Bell className="h-6 w-6" />
                  <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-500 ring-2 ring-gray-800"></span>
                </div>
              </button>
              
              {/* Notifications dropdown */}
              {isNotificationsOpen && (
                <div className="origin-top-right absolute right-0 mt-2 w-80 rounded-md shadow-lg bg-gray-800 ring-1 ring-black ring-opacity-5">
                  <div className="py-2">
                    <div className="px-4 py-2 border-b border-gray-700">
                      <p className="text-sm font-medium text-white">Notifications</p>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {/* Notification items */}
                      {[1, 2, 3].map((item) => (
                        <div key={item} className="px-4 py-3 hover:bg-gray-700 border-b border-gray-700">
                          <div className="flex items-start">
                            <div className="flex-shrink-0 bg-red-500 rounded-full p-1">
                              <Info className="h-4 w-4 text-white" />
                            </div>
                            <div className="ml-3 w-0 flex-1">
                              <p className="text-sm font-medium text-white">Alert detected</p>
                              <p className="mt-1 text-sm text-gray-300">
                                High severity threat detected in system {item}
                              </p>
                              <p className="mt-1 text-xs text-gray-400">5m ago</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="px-4 py-2 border-t border-gray-700">
                      <button className="text-sm text-indigo-400 hover:text-indigo-300 font-medium">
                        View all notifications
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Help Center */}
            <div className="relative" ref={helpRef}>
              <button 
                onClick={() => setIsHelpOpen(!isHelpOpen)}
                className="text-gray-300 hover:text-white p-1 rounded-full hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-indigo-500"
              >
                <HelpCircle className="h-6 w-6" />
              </button>
              
              {/* Help Center dropdown */}
              {isHelpOpen && (
                <div className="origin-top-right absolute right-0 mt-2 w-64 rounded-md shadow-lg bg-gray-800 ring-1 ring-black ring-opacity-5">
                  <div className="py-1">
                    <div className="px-4 py-3 border-b border-gray-700">
                      <p className="text-sm font-medium text-white">Help Center</p>
                    </div>
                    <button className="w-full text-left block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white">
                      Documentation
                    </button>
                    <button className="w-full text-left block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white">
                      Training Videos
                    </button>
                    <button className="w-full text-left block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white">
                      Contact Support
                    </button>
                    <button className="w-full text-left block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white">
                      FAQs
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            {/* Profile dropdown */}
            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-indigo-500"
              >
                <span className="sr-only">Open user menu</span>
                <div className="flex items-center gap-2">                 
                            {currentUser ? (
                                                <>
                                                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center text-white text-sm font-medium">
                                                    <Avatar firstName={currentUser.first_name} lastName={currentUser.last_name} />
                                                  </div>
                                                  <div>
                                                    <span className="text-sm text-gray-100 font-medium">{currentUser.first_name} {currentUser.last_name}</span>
                                               
                                                  </div>
                                                </>
                                              ) : (
                                                <div className="flex items-center gap-2">
                                                  <Skeleton width="w-10 h-10" rounded="rounded-full" /> {/* Skeleton for Avatar */}
                                                  <div className="flex flex-col">
                                                    <Skeleton width="w-20" height="h-3" rounded="rounded-md" /> {/* Skeleton for Name */}
                                                    <Skeleton width="w-16" height="h-3" rounded="rounded-md" /> {/* Skeleton for Role */}
                                                  </div>
                                                </div>
                                              )}
                </div>
                <ChevronDown className="h-5 w-5 text-gray-300" />

              </button>
              
              {/* Profile dropdown panel */}
              {isProfileOpen && (
                <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-gray-800 ring-1 ring-black ring-opacity-5 py-1">
                  <button className="w-full text-left block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white">
                    Your Profile
                  </button>
                  <button className="w-full text-left flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white">
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </button>
                  <button 
                    onClick={handleLogout}
                    className="w-full text-left flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
          
          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
            >
              <span className="sr-only">Open main menu</span>
              {isOpen ? (
                <X className="block h-6 w-6" />
              ) : (
                <Menu className="block h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="md:hidden bg-gray-800 pb-3 pt-2">
          <div className="px-2 space-y-1">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className={`block px-3 py-2 rounded-md text-base font-medium ${
                  link.current
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                {link.name}
              </a>
            ))}
            <div className="mt-4 pt-4 border-t border-gray-700">
              <button
                onClick={() => setIsHelpOpen(!isHelpOpen)}
                className="w-full text-left block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:bg-gray-700 hover:text-white"
              >
                <div className="flex items-center">
                  <HelpCircle className="mr-3 h-5 w-5" />
                  Help Center
                </div>
              </button>
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="w-full text-left block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:bg-gray-700 hover:text-white"
              >
                <div className="flex items-center">
                  <User className="mr-3 h-5 w-5" />
                  Your Profile
                </div>
              </button>
              <button
                onClick={handleLogout}
                className="w-full text-left block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:bg-gray-700 hover:text-white"
              >
                <div className="flex items-center">
                  <LogOut className="mr-3 h-5 w-5" />
                  Logout
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;